import { useState, useEffect, useRef } from 'react';
import { useTrackEvent } from '@wkda/funnel-components';

/**
 * Custom hook for managing scroll navigation in a horizontal slider
 * @param {number} totalItems - Total number of items in the slider
 * @returns {object} - Scroll navigation state and methods
 */
export function useScrollNavigation(totalItems) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxScrollIndex, setMaxScrollIndex] = useState(0);
  const containerRef = useRef(null);
  const track = useTrackEvent();
  const isProgrammaticScroll = useRef(false); // if scrolled by button / dots
  const scrollTimeoutRef = useRef(null);
  const scrollDebounceRef = useRef(null);
  const lastScrollPosition = useRef(0);

  // Calculate maximum scrollable positions based on viewport
  const calculateMaxScrollPositions = () => {
    if (!containerRef.current) return 1;
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const scrollWidth = container.scrollWidth;
    if (containerWidth <= 0 || scrollWidth <= 0 || totalItems <= 0) {
      return 1;
    }

    // For modal/popup scenarios, ensure we have valid dimensions
    if (containerWidth < 200) {
      return 1;
    }

    // Calculate how many cards can fit in the viewport
    const cardWidth = scrollWidth / totalItems;
    const cardsPerView = Math.floor(containerWidth / cardWidth);

    // Maximum scroll positions = total cards - cards that fit in viewport
    const maxPositions = Math.max(1, totalItems - cardsPerView + 1);

    // Ensure we don't exceed total cards
    return Math.min(maxPositions, totalItems);
  };

  // Update max scroll positions when component mounts or resizes
  useEffect(() => {
    const updateMaxScroll = () => {
      const maxPos = calculateMaxScrollPositions();
      const safeMaxIndex = Math.max(0, maxPos - 1);
      setMaxScrollIndex(safeMaxIndex);
    };

    // Initial calculation
    const timeoutId = setTimeout(updateMaxScroll, 100);

    // Also try immediately if container is already available
    if (containerRef.current) {
      updateMaxScroll();
    }
    window.addEventListener('resize', updateMaxScroll);
    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
      window.removeEventListener('resize', updateMaxScroll);
    };
  }, [totalItems]);

  // Handle scroll events
  const handleScroll = e => {
    const scrollLeft = e.target.scrollLeft;
    const scrollWidth = e.target.scrollWidth;
    const clientWidth = e.target.clientWidth;

    // Track swipe events (only for user-initiated swipes, not programmatic aka by buttons or dots)
    if (!isProgrammaticScroll.current) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce the tracking to avoid spam during continuous scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        track({
          action: 'eventTracking',
          category: 'lead',
          eventData: {
            category: 'Google Ratings',
            action: 'Swipe',
            label: 'Google Reviews Swipe'
          }
        });
      }, 300);
    }

    // Debounce the currentIndex calculation to prevent rapid state changes
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      // Check if scroll position has changed significantly (threshold of 5px)
      const scrollDifference = Math.abs(scrollLeft - lastScrollPosition.current);
      if (scrollDifference < 5 && scrollLeft !== 0) {
        return; // Ignore micro-adjustments
      }
      lastScrollPosition.current = scrollLeft;

      // Check if we're at the very end of the scroll
      if (scrollLeft + clientWidth >= scrollWidth - 1) {
        setCurrentIndex(maxScrollIndex);
        return;
      }
      const cardWidth = scrollWidth / totalItems;
      const newIndex = Math.round(scrollLeft / cardWidth);
      const clampedIndex = Math.min(newIndex, maxScrollIndex);

      // Only update if the index actually changed
      setCurrentIndex(prevIndex => {
        if (prevIndex !== clampedIndex) {
          return clampedIndex;
        }
        return prevIndex;
      });
    }, 50); // 50ms debounce for smooth scrolling
  };

  // Scroll to a specific card
  const scrollToCard = index => {
    if (containerRef.current) {
      const cardWidth = containerRef.current.scrollWidth / totalItems;
      const scrollPosition = cardWidth * index;

      // Clear any pending debounced updates
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }

      // Mark this as a programmatic scroll
      isProgrammaticScroll.current = true;

      // Update the last scroll position to prevent conflicts
      lastScrollPosition.current = scrollPosition;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setCurrentIndex(index);

      // Reset the flag after scroll completes
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 600);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToCard(index);
    }
  };
  const scrollToPrevious = () => {
    if (currentIndex > 0) {
      scrollToCard(currentIndex - 1);
    }
  };
  const scrollToNext = () => {
    if (currentIndex < maxScrollIndex) {
      scrollToCard(currentIndex + 1);
    }
  };
  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < maxScrollIndex;

  // Force recalculation
  const forceRecalculation = () => {
    const maxPos = calculateMaxScrollPositions();
    const safeMaxIndex = Math.max(0, maxPos - 1);
    setMaxScrollIndex(safeMaxIndex);
  };
  return {
    currentIndex,
    maxScrollIndex,
    containerRef,
    handleScroll,
    scrollToCard,
    handleKeyDown,
    scrollToPrevious,
    scrollToNext,
    canScrollLeft,
    canScrollRight,
    forceRecalculation
  };
}